import { faArrowRight } from '@fortawesome/pro-solid-svg-icons'
import { useIntl } from 'react-intl'

import {
  AnalyticsEntityType,
  AnalyticsSource,
} from '@b-stock/fe-core/bstock-react'

import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

import { useListViewDealBadgeProps } from '../../helpers/useListViewDealBadgeProps'
import { useActionPanel } from '../ActionPanelProvider'
import { ActionFormButton } from './ActionFormButton'
import { FormRow } from './ui'

const formNs = 'Listings.ListView.actionPanel.form'

/**
 * View Details as the panel renders it: a button filling the form, not a link
 * sitting in the corner of the cell.
 *
 * This is a state of the form rather than an action in the menu — it occupies
 * the same slot as the amount input and CTA, on every row that cannot bid from
 * the list. That is a wide set: ended lots, offerings, contracts, non-3MP
 * listings, buyers not eligible for the seller, and rows whose terms have not
 * been accepted. Its description has to hold for all of them, so it names
 * neither a reason nor any content the lot might not have.
 *
 * `ViewDetailsCell` is deliberately untouched — it is also the SENTINEL renderer
 * for the Quantity, Unit Price, Total MSRP and Current Bid columns, which must
 * keep showing a plain link.
 */
export const ViewDetailsButton = ({
  listing,
}: {
  listing: CombinedListingSearchResult
}) => {
  const { formatMessage } = useIntl()
  const { trackButtonClicked } = useAnalytics()
  const dealBadgeProps = useListViewDealBadgeProps(listing)
  const { surface, variant } = useActionPanel()
  const iconOnly = variant === 'narrow'

  const handleClick = () => {
    trackButtonClicked({
      screenName: 'all_auction_list_view',
      buttonName: 'view_details',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      listing_id: listing.listingId,
      user_bid_status: listing.bidStatus,
      ...surface,
      ...dealBadgeProps,
    })
  }

  return (
    <FormRow>
      <ActionFormButton
        description={formatMessage({ id: `${formNs}.description.viewDetails` })}
        href={listing.auctionUrl}
        icon={faArrowRight}
        iconOnly={iconOnly}
        label={formatMessage({ id: 'Auction.BidAmount.viewDetails' })}
        onClick={handleClick}
      />
    </FormRow>
  )
}
